import { useRouter } from "next/router";
import { useEffect, useState } from "react";
import { UseFormReturn, useForm, useWatch } from "react-hook-form";

import PageNav from "../components/common/pageNav.comp";
import AddLink from "../components/form/addLink.comp";
import OutlineBtn from "../components/form/outlineBtn.comp";
import Layout from "../components/layouts/layout.comp";
import ProductList from "../components/product/productList.comp";
import ProductListFilterComp from "../components/product/productListFilter.comp";
import Error from "../components/reuseables/error.comp";
import { getDontHandleError, post } from "../core/fetch.core";
import testCore from "../core/test.core";
import { useTranslation } from "../core/translation.core";
import useProductQuery from "../hooks/useProductQuery";
import { ProductListFilter } from "../types/product";
import { UserPreference } from "../types/user";

import ProductExport from "../components/product/productExport.comp";
import BatchSelectionToolbar from "../components/product/batchSelectionToolbar.comp";

export default function ProductsPage() {
  const { t, msg } = useTranslation();

  return (
    <Layout title={t(msg.product.products)} titleBtn={<TitleBtns />}>
      <Error />
      <Content />
    </Layout>
  );
}

function TitleBtns() {
  return (
    <div className="flex gap-2 items-center">
      <AddLink id="product-create" href="/product/create" />
      <ProductExport />
      <CloneProductBtn />
    </div>
  );
}

function useProductListFilterForm() {
  const { query, isReady } = useRouter();
  const [isLoadingReady, setIsLoadingReady] = useState(false);

  const methods = useForm<ProductListFilter>({
    defaultValues: {
      page: 1,
      pageSize: 20,
      sort: "name",
      sortAsec: 1,
      categoryIds: [],
      brandIds: [],
      tagId: [],
      withInventories: false,
      productTypes: [],
    },
  });

  // sync the page param back to form hook
  useEffect(() => {
    methods.setValue("page", Number(query.page ?? 1));

    // eslint-disable-next-line
  }, [query, isReady]);

  // userPreference ready
  useEffect(() => {
    loadUserPreference().then(() => {
      // delay this a little bit for the useWatch to trigger first
      setTimeout(() => {
        setIsLoadingReady(true);
      }, 11);
    });

    // eslint-disable-next-line
  }, []);

  const watch = useWatch({ control: methods.control });

  // userPreference/add
  useEffect(() => {
    if (!isReady) return;
    if (!isLoadingReady) return;

    const preference = { ...watch };
    delete preference.page;
    delete preference.withInventories;

    post("/admin/userPreference/add", {
      type: "productFilter",
      preference,
    });

    // eslint-disable-next-line
  }, [watch]);

  async function loadUserPreference() {
    const res = await getDontHandleError<UserPreference>("/admin/userPreference/get?type=productFilter");

    if (res.status === 404) return;
    if (!res.data) return;

    const [page] = methods.getValues(["page"]);

    if (!res.data.preference) return;

    const savedPageSize = res.data.preference.pageSize ?? methods.getValues("pageSize");

    methods.reset({ ...res.data.preference, page, pageSize: savedPageSize, withInventories: false });
  }

  if (!isReady) return null;
  if (!isLoadingReady) return null;

  return methods;
}

export type ProductListColumnKey =
  | "image"
  | "name"
  | "sku"
  | "price2"
  | "category"
  | "inventory"
  | "price"
  | "status"
  | "cost"
  | "brand"
  | "createdAt"
  | "updatedAt";

export type CheckedItemsState = {
  [K in ProductListColumnKey]: boolean;
};

function Content() {
  const router = useRouter();
  const methods = useProductListFilterForm();
  const [checkedItems, setCheckedItems] = useState<CheckedItemsState>({
    image: true,
    name: true,
    sku: true,
    price2: true,
    category: true,
    inventory: true,
    price: false,
    status: false,
    cost: false,
    brand: false,
    createdAt: false,
    updatedAt: false,
  });
  const [selectedProductIds, setSelectedProductIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    const savedPreferences = localStorage.getItem("checkedItems");

    if (savedPreferences) {
      const parsedPreferences = JSON.parse(savedPreferences);

      const updatedPreferences: typeof checkedItems = {
        image: parsedPreferences.image ?? checkedItems.image,
        name: parsedPreferences.name ?? checkedItems.name,
        sku: parsedPreferences.sku ?? checkedItems.sku,
        price2: parsedPreferences.price2 ?? checkedItems.price2,
        category: parsedPreferences.category ?? checkedItems.category,
        inventory: parsedPreferences.inventory ?? checkedItems.inventory,
        price: parsedPreferences.price ?? checkedItems.price,
        status: parsedPreferences.status ?? checkedItems.status,
        cost: parsedPreferences.cost ?? checkedItems.cost,
        brand: parsedPreferences.brand ?? checkedItems.brand,
        createdAt: parsedPreferences.createdAt ?? checkedItems.createdAt,
        updatedAt: parsedPreferences.updatedAt ?? checkedItems.updatedAt,
      };

      setCheckedItems(updatedPreferences);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    localStorage.setItem("checkedItems", JSON.stringify(checkedItems));
  }, [checkedItems]);

  if (!methods) return null;

  return (
    <>
      <ProductListFilterComp methods={methods} />
      <CheckboxComponent checkedItems={checkedItems} setCheckedItems={setCheckedItems} />
      <BatchSelectionToolbar selectedProductIds={selectedProductIds} setSelectedProductIds={setSelectedProductIds} />
      <section className="bg-white dark:bg-her-darkSectionBg shadow-sm py-2 mb-6 rounded-2xl dark:text-slate-300">
        <ProductList
          methods={methods}
          checkedItems={checkedItems}
          selectedProductIds={selectedProductIds}
          setSelectedProductIds={setSelectedProductIds}
        />
      </section>
      <Paging methods={methods} />
    </>
  );
}

function CloneProductBtn() {
  if (process.env.NODE_ENV !== "development") return null;

  async function clone() {
    await testCore.clone100Products();
  }

  return (
    <OutlineBtn id="clone-100-products" onClick={clone}>
      Clone 100 Products
    </OutlineBtn>
  );
}

function Paging({ methods }: { methods: UseFormReturn<ProductListFilter, any> }) {
  const router = useRouter();
  const [pageSize] = useWatch({
    name: ["pageSize"],
    control: methods.control,
  });
  const { data: products } = useProductQuery(methods);

  const total = products?.size;

  function handlePageSizeChange(newPageSize: number) {
    methods.setValue("pageSize", newPageSize);
    if (router.query.page && Number(router.query.page) > 1) {
      router.push({
        pathname: "/products",
        query: { page: 1 },
      });
    }
  }

  return (
    <PageNav
      page={Number(router.query.page) || 1}
      size={pageSize}
      total={total ?? 0}
      pathname="/products"
      query={router.query}
      onPageSizeChange={handlePageSizeChange}
    />
  );
}

interface CheckboxProps {
  checkedItems: CheckedItemsState;
  setCheckedItems: React.Dispatch<React.SetStateAction<CheckedItemsState>>;
}

const CheckboxComponent = ({ checkedItems, setCheckedItems }: CheckboxProps) => {
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCheckedItems((prev) => ({
      ...prev,
      [e.target.name]: e.target.checked,
    }));
  };

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 w-full">
      {Object.keys(checkedItems).map((key: ProductListColumnKey) => (
        <label key={key} className="flex items-center cursor-pointer">
          <input type="checkbox" name={key} checked={checkedItems[key]} onChange={handleCheckboxChange} className="hidden" />
          <div
            className={`w-4 h-4 rounded border-2 ${
              checkedItems[key] ? "bg-blue-500 border-blue-500" : "border-gray-400"
            } flex items-center justify-center`}
          >
            {checkedItems[key] && (
              <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M5 13l4 4L19 7"></path>
              </svg>
            )}
          </div>
          <span className="ml-1 capitalize text-[12px]">
            <ColName name={key} />
          </span>
        </label>
      ))}
    </div>
  );
};

function ColName({ name }: { name: ProductListColumnKey }) {
  const { t, msg } = useTranslation();

  let text: string;
  switch (name) {
    case "image":
      text = t(msg.product.image);
      break;
    case "name":
      text = t(msg.product.name);
      break;
    case "sku":
      text = t(msg.product.sku);
      break;
    case "price2":
      text = t(msg.product.price2);
      break;
    case "category":
      text = t(msg.category.category);
      break;
    case "inventory":
      text = t(msg.inventory.inventory);
      break;
    case "price":
      text = t(msg.product.price);
      break;
    case "status":
      text = t(msg.product.productStatus);
      break;
    case "cost":
      text = t(msg.report.cost);
      break;
    case "brand":
      text = t(msg.product.brand);
      break;
    case "createdAt":
      text = t(msg.import.createdAt);
      break;
    case "updatedAt":
      text = t(msg.import.updatedAt);
      break;
    default:
      text = name;
  }
  return <span>{text}</span>;
}
